import { ElementRef, HostListener } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { ClassEntityService } from '../../services/entity-services/classes-entity.service';
import { NotificationService } from '../../services/notification.service';
import { LaunchSessionStoreService } from '../../services/launch-session-store.service';
import { UnifiedVmLauncherService } from '../../services/unified-vm-launcher.service';
import { RightsCases } from '../../models/rights-cases-enum';
import { UserEntityService } from '../../services/settings/user-entity.service';
import { Log, IShortLogger } from '../../services/logger.service';
import { ConfirmDialogService } from '../../services/confirm-dialog.service';


@Component({
  selector: 'app-class-resources',
  templateUrl: './class-resources.component.html',
  styleUrl: './class-resources.component.css'
})
export class ClassResourcesComponent implements OnInit {
  private readonly logger: IShortLogger<ClassResourcesComponent> = Log.create(ClassResourcesComponent);
  public templateHasUserSubscription: boolean = true;

  isVisibleDeteleResourceButton: boolean = false;

  constructor(
    private classResourcesService: ClassEntityService,
    private route: ActivatedRoute,
    private router: Router,
    private launchSessionStoreService: LaunchSessionStoreService,
    private unifiedLauncher: UnifiedVmLauncherService,
    private userService: UserEntityService,
    private confirmDialog: ConfirmDialogService,
    private notification: NotificationService
  ) { }
  openedMenu: string | null = null;
  openedVMMenu: string | null = null;
  manageAllMenuOpen: boolean = false;

  isVisibleLaunchButton: boolean = false;
  isVisibleDeleteButton: boolean = false;
  // Toggle ellipsis menu for a template
  toggleMenu(template: any, event: MouseEvent) {
    this.isVisibleLaunchButton = false;
    this.isVisibleDeleteButton = false;
    if (template.subscriptionId) {
      this.isVisibleDeleteButton = true;
      // this.isVisibleDeteleResourceButton
      if (template.templateType.toLowerCase() === 'vm') {
        this.isVisibleLaunchButton = true;
      }
    }

    event.stopPropagation();

    if (this.openedMenu === template.id) {
      this.openedMenu = null;
    } else {
      this.openedMenu = template.id;
    }
  }

  // Close the ellipsis menu
  closeMenu() {
    this.openedMenu = null;
  }

  toggleManageAllMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.manageAllMenuOpen = !this.manageAllMenuOpen;
  }

  // Close menu on outside click
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.openedMenu = null;
    this.openedVMMenu = null;
    this.manageAllMenuOpen = false;
  }

  // Per-row VM ellipsis menu toggle
  toggleVMMenu(vm: any, event: MouseEvent): void {
    event.stopPropagation();
    this.openedVMMenu = this.openedVMMenu === vm.userId ? null : vm.userId;
  }

  closeVMMenu(): void {
    this.openedVMMenu = null;
  }

  // ── VM Power Actions ───────────────────────────────────────────────────────

  shutdownVM(vm: any): void {
    this.closeVMMenu();
    this.confirmDialog.typedConfirm({
      title: `Shutdown VM for ${vm.participant}`,
      message: `This will gracefully shut down the guest OS for VM "${vm.vmName}". ` +
               `The VM will remain allocated and Azure charges will continue.`,
      type: 'warning',
      confirmPhrase: 'SHUTDOWN',
      confirmButtonText: 'Shutdown VM'
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.loading = true;
      this.classResourcesService.shutdownVM(this.classId, vm.userId).subscribe({
        next: () => { this.loading = false; this.notification.notify(`VM "${vm.vmName}" is shutting down.`, 'success', 3000); },
        error: (err) => { this.loading = false; this.logger.e('Shutdown failed:', err); this.notification.notify('Failed to shutdown VM', 'error', 3000); }
      });
    });
  }

  shutdownAllVMs(): void {
    this.confirmDialog.typedConfirm({
      title: `Shutdown All VMs`,
      message: `This will gracefully shut down the guest OS on all ${this.userVMs?.length ?? 0} VM(s) in this class. ` +
               `VMs will remain allocated and Azure charges will continue.`,
      type: 'warning',
      confirmPhrase: 'SHUTDOWN ALL',
      confirmButtonText: 'Shutdown All VMs'
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.loading = true;
      this.classResourcesService.shutdownAllVMs(this.classId).subscribe({
        next: () => { this.loading = false; this.notification.notify('All VMs are shutting down.', 'success', 3000); },
        error: (err) => { this.loading = false; this.logger.e('Shutdown all failed:', err); this.notification.notify('Failed to shutdown all VMs', 'error', 3000); }
      });
    });
  }

  deallocateVM(vm: any): void {
    this.closeVMMenu();
    this.confirmDialog.typedConfirm({
      title: `Deallocate VM for ${vm.participant}`,
      message: `This will stop and deallocate VM "${vm.vmName}". ` +
               `The VM will be powered off without a guest OS signal. Azure compute charges will pause but storage charges continue.`,
      type: 'warning',
      confirmPhrase: 'DEALLOCATE',
      confirmButtonText: 'Deallocate VM'
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.loading = true;
      this.classResourcesService.deallocateVM(this.classId, vm.userId).subscribe({
        next: () => { this.loading = false; this.notification.notify(`VM "${vm.vmName}" is being deallocated.`, 'success', 3000); },
        error: (err) => { this.loading = false; this.logger.e('Deallocate failed:', err); this.notification.notify('Failed to deallocate VM', 'error', 3000); }
      });
    });
  }

  deallocateAllVMs(): void {
    this.confirmDialog.typedConfirm({
      title: `Deallocate All VMs`,
      message: `This will stop and deallocate all ${this.userVMs?.length ?? 0} VM(s) in this class without a guest OS signal. ` +
               `Azure compute charges will pause.`,
      type: 'warning',
      confirmPhrase: 'DEALLOCATE ALL',
      confirmButtonText: 'Deallocate All VMs'
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.loading = true;
      this.classResourcesService.deallocateAllVMs(this.classId).subscribe({
        next: () => { this.loading = false; this.notification.notify('All VMs are being deallocated.', 'success', 3000); },
        error: (err) => { this.loading = false; this.logger.e('Deallocate all failed:', err); this.notification.notify('Failed to deallocate all VMs', 'error', 3000); }
      });
    });
  }

  shutdownAndDeallocateVM(vm: any): void {
    this.closeVMMenu();
    this.confirmDialog.typedConfirm({
      title: `Stop VM for ${vm.participant}`,
      message: `This will gracefully shut down the guest OS then deallocate VM "${vm.vmName}" ` +
               `(equivalent to Azure Portal "Stop"). Azure compute charges will pause.`,
      type: 'warning',
      confirmPhrase: 'STOP',
      confirmButtonText: 'Stop VM'
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.loading = true;
      this.classResourcesService.shutdownAndDeallocateVM(this.classId, vm.userId).subscribe({
        next: () => { this.loading = false; this.notification.notify(`VM "${vm.vmName}" is stopping.`, 'success', 3000); },
        error: (err) => { this.loading = false; this.logger.e('Stop failed:', err); this.notification.notify('Failed to stop VM', 'error', 3000); }
      });
    });
  }

  shutdownAndDeallocateAllVMs(): void {
    this.confirmDialog.typedConfirm({
      title: `Stop All VMs`,
      message: `This will gracefully shut down then deallocate all ${this.userVMs?.length ?? 0} VM(s) in this class ` +
               `(equivalent to Azure Portal "Stop"). Azure compute charges will pause.`,
      type: 'warning',
      confirmPhrase: 'STOP ALL',
      confirmButtonText: 'Stop All VMs'
    }).subscribe(result => {
      if (!result.confirmed) return;
      this.loading = true;
      this.classResourcesService.shutdownAndDeallocateAllVMs(this.classId).subscribe({
        next: () => { this.loading = false; this.notification.notify('All VMs are stopping.', 'success', 3000); },
        error: (err) => { this.loading = false; this.logger.e('Stop all failed:', err); this.notification.notify('Failed to stop all VMs', 'error', 3000); }
      });
    });
  }

  // Launch template using unified launcher
  launchTemplate(template: any) {
    if (!this.classId) {
      this.logger.e('Class ID is missing for template launch');
      this.notification.notify('Class ID is missing', 'error', 3000);
      return;
    }

    if (!template || !template.id) {
      this.logger.e('Template ID is missing for template launch');
      this.notification.notify('Template ID is missing', 'error', 3000);
      return;
    }

    // Get user information from localStorage
    const userId = localStorage.getItem('userId') || '';
    const userName = localStorage.getItem('userName') || '';
    const roles = localStorage.getItem('roles') || '';
    const userRole = roles ? JSON.parse(roles)[0] : 'trainer';

    if (!userId || !userName) {
      this.logger.e('User information is missing');
      this.notification.notify('User information is missing', 'error', 3000);
      return;
    }

    this.logger.i('Launching template with ID:', template.id, 'for class:', this.classId);

    // Use unified launcher for template launch
    const preOpenedWindow = window.open('', '_blank');
    this.unifiedLauncher.launchClassTemplateVm({
      userId,
      templateId: template.id,
      classId: this.classId,
      userName,
      vmConnectionTemplateId: this.classVmConnectionTemplateId,
    }, preOpenedWindow);
  }

  // Sample method: Create snapshot
  public createSnapshot(template: any) {
    this.classResourcesService.createSnapshot(template.id, this.classId).subscribe({
      next: (response) => {
        this.getResourcesByClassId();
        this.notification.notify('Snapshot created successfully', 'success', 3000);
        // Optionally refresh data or update UI here
      },
      error: (error) => {
        this.logger.e('Snapshot creation failed:', error);
        this.notification.notify('Failed to create snapshot', 'error', 3000);
      }
    });
  }

  // Delete template with confirmation
  deleteTemplate(template: any) {
    if (!template) {
      this.notification.notify('Invalid template data', 'error', 3000);
      return;
    }

    this.confirmDialog.typedConfirm({
      title: `Delete Template "${template.templateName}"`,
      message: `You are about to permanently delete this template. This action is irreversible and will remove the template, ` +
               `its associated snapshot, and all template configurations.`,
      details: [
        `Template Name: ${template.templateName}`,
        `Template Type: ${template.templateType}`,
        `VM Name: ${template.vmName}`,
        `Snapshot: ${template.snapshotName}`
      ],
      type: 'error',
      confirmPhrase: 'DELETE',
      confirmButtonText: 'Delete Template'
    }).subscribe(result => {
      if (!result.confirmed) {
        this.notification.notify('Template deletion cancelled', 'info', 2000);
        return;
      }
      this.executeDeleteTemplate(template);
    });
  }

  // Execute the actual deletion of a specific template
  private executeDeleteTemplate(template: any) {
    this.notification.notify(`Starting deletion of template "${template.templateName}"...`, 'warning', 3000);

    // Show loading state
    this.loading = true;

    // Call the API to destroy the template
    if (template?.templateType.toLowerCase() === 'vm') {
      this.classResourcesService.destroyTemplate(template.id, this.classId).subscribe({
        next: (response) => {
          this.loading = false;
          // Remove the deleted template from the list
          this.templates = this.templates.filter(templateItem => templateItem.id !== template.id);
          this.notification.notify(`🎉 Successfully deleted template "${template.templateName}"!`, 'success', 4000);
          // Optionally refresh the data
          this.getResourcesByClassId();
        },
        error: (error) => {
          this.loading = false;
          this.logger.e('Failed to delete template:', error);
          this.notification.notify(`Failed to delete template "${template.templateName}"`, 'error', 3000);
        }
      });
    }
    else if (template?.templateType.toLowerCase() === 'acc') {
      this.classResourcesService.destroyTemplateUserAccount(template.id, this.classId).subscribe({
        next: (response) => {
          this.loading = false;
          // Remove the deleted template from the list
          this.templates = this.templates.filter(templateItem => templateItem.id !== template.id);
          this.notification.notify(`🎉 Successfully deleted template "${template.templateName}"!`, 'success', 4000);
          // Optionally refresh the data
          this.getResourcesByClassId();
        },
        error: (error) => {
          this.loading = false;
          this.logger.e('Failed to delete template:', error);
          this.notification.notify(`Failed to delete template "${template.templateName}"`, 'error', 3000);
        }
      });
    }


  }
  // Navigate back to ClassesComponent
  backToClass() {
    // window.location.href = '/classes';
    this.router.navigate(['/classes']);
  }
  classDetails: string = '';
  templates: any[] = [];
  /**
   * VM Connection Template id currently attached to this class (or null when none).
   * Fetched once per page load alongside the resources list and forwarded into the
   * class-template-vm launcher so the encrypted Guacamole token reflects the operator's
   * selection rather than the global appsettings.json defaults.
   */
  classVmConnectionTemplateId: string | null = null;
  userVMs: any[] = [];
  userAccounts: any[] = [];
  costingData: any[] = [];
  subscriptions: any[] = []; // New: List of subscriptions
  selectedSubscription: any = null;
  selectedResources: any[] = [];
  loading: boolean = true;
  activeTab: string = 'templates';
  viewMode: 'detail' | 'card' = 'detail';
  classId: string = '';

  // Subscription Usage data
  totalUsage: any = {};
  userWiseUsage: any[] = [];
  userWiseResources: any[] = [];
  selectedUser: any = null;



  ngOnInit(): void {
    this.getResourcesByClassId();
    this.getRightsByCases();
    // initializeSubscriptionUsageData is now driven by getResourcesByClassId → loadUserAccountUsage
  }

  public getResourcesByClassId() {
    this.route.queryParams.subscribe(params => {

      this.classId = params['classId'] || '';
      if (this.classId) {
        this.classResourcesService.getClassResources(this.classId).subscribe((data: any) => {
          this.logger.i('Class resources data:', data);
          this.classDetails = data.classDetails;
          this.templates = data.templates;
          this.userVMs = data.userVMs;
          this.userAccounts = data.userAccounts;
          this.templates.find(t => t.templateType.toLowerCase() === 'acc') ? this.templateHasUserSubscription = true : this.templateHasUserSubscription = false;
          // Load costing data instead of initializing mock data
          this.getCostingData();
          // Load real user-account usage data
          this.loadUserAccountUsage();

          this.loading = false;
        });

        // Fetch the class document once so we know which VM Connection Template (if any) is
        // currently attached. The result is forwarded into every class-template-vm launch
        // below; absence (null) preserves the pre-template global-defaults behaviour.
        this.classResourcesService.getClassByClassId(this.classId).subscribe({
          next: (entity: any) => {
            this.classVmConnectionTemplateId =
              entity?.vmConnectionTemplateId
              ?? entity?.data?.['VmConnectionTemplateId']
              ?? null;
          },
          error: (err: any) => {
            this.logger.w('Failed to load class for vm-connection-template id resolution; falling back to global defaults', err);
            this.classVmConnectionTemplateId = null;
          },
        });
      }
    });

  }

  setTab(tab: string) {
    this.activeTab = tab;
  }

  public getCostingData() {
    if (!this.classId) {
      this.notification.notify('Class ID is missing', 'error', 3000);
      return;
    }
    this.classResourcesService.getCostingData(this.classId).subscribe({
      next: (response: any) => {
        this.costingData = response.data || [];
        this.processSubscriptions();
        this.notification.notify('Costing data loaded successfully', 'success', 2000);
      },
      error: (error) => {
        this.logger.e('Failed to load costing data:', error);
        this.notification.notify('Failed to load costing data', 'error', 3000);
      }
    });
  }

  // Process the API response to create subscription list
  processSubscriptions() {
    const subscriptionMap = new Map();

    this.costingData.forEach(item => {
      if (!subscriptionMap.has(item.subscriptionId)) {
        subscriptionMap.set(item.subscriptionId, {
          subscriptionId: item.subscriptionId,
          subscriptionType: item.subscriptionType,
          totalCostINR: parseFloat(item.totalCostINR),
          totalCostOriginalCurrency: parseFloat(item.totalCostOriginalCurrency),
          originalCurrency: item.originalCurrency,
          exchangeRateToINR: item.exchangeRateToINR,
          asOfUtc: item.asOfUtc,
          timeframe: item.timeframe,
          resourceCount: item.resources ? item.resources.length : 0,
          resources: item.resources || []
        });
      }
    });

    this.subscriptions = Array.from(subscriptionMap.values());
  }

  viewTemplateDetails(template: any) {
    // TODO: Implement view details logic
    alert('View details for template: ' + template.id);
  }

  destroyTemplate(template: any) {
    // TODO: Implement destroy logic
    alert('Destroy template: ' + template.id);
  }

  viewVMDetails(vm: any) {
    // TODO: Implement view details logic
    alert('View details for VM: ' + vm.vmName);
  }

  destroyVM(vm: any) {
    if (!vm) {
      this.notification.notify('Invalid VM data', 'error', 3000);
      return;
    }

    this.confirmDialog.typedConfirm({
      title: `Destroy VM for ${vm.participant}`,
      message: `You are about to permanently destroy the VM "${vm.vmName}" for participant "${vm.participant}". ` +
               `This action is irreversible and will delete the virtual machine, release the IP address, and remove the network interface.`,
      details: [
        `Participant: ${vm.participant}`,
        `VM Name: ${vm.vmName}`,
        `IP Address: ${vm.publicIpAddress}`
      ],
      type: 'warning',
      confirmPhrase: 'DESTROY',
      confirmButtonText: 'Destroy VM',
      secondaryOption: {
        phrase: 'DESTROY WITH DISK',
        buttonText: 'Destroy with OS Disk'
      }
    }).subscribe(result => {
      if (!result.confirmed) {
        this.notification.notify('VM destruction cancelled', 'info', 2000);
        return;
      }
      const destroyOSDisk = result.selectedOption === 'secondary';
      this.executeDestroyVM(vm, destroyOSDisk);
    });
  }

  // Execute the actual destruction of a specific VM
  private executeDestroyVM(vm: any, destroyOSDisk: boolean) {
    this.notification.notify(`Starting destruction of VM "${vm.vmName}" for user "${vm.userId}"...`, 'warning', 3000);

    // Show loading state
    this.loading = true;

    // Call the API to destroy the specific VM (using participant/student identifier)
    this.classResourcesService.destroyVM(this.classId, vm.userId, destroyOSDisk).subscribe({
      next: (response) => {
        this.loading = false;
        // Remove the destroyed VM from the list
        this.userVMs = this.userVMs.filter(vmItem => vmItem.userId !== vm.userId);
        this.notification.notify(`🎉 Successfully destroyed VM "${vm.vmName}" for user "${vm.userId}"!`, 'success', 4000);
        // Optionally refresh the data
        this.getResourcesByClassId();
      },
      error: (error) => {
        this.loading = false;
        this.logger.e('Failed to destroy VM:', error);
        this.notification.notify(`Failed to destroy VM "${vm.vmName}"`, 'error', 3000);
      }
    });
  }

  public destroyOSDisk: boolean = false;

  // Destroy all VMs and resources in the current class
  destroyAllResources() {
    if (!this.userVMs || this.userVMs.length === 0) {
      this.notification.notify('No resources found to destroy', 'warning', 3000);
      return;
    }

    this.confirmDialog.typedConfirm({
      title: `Destroy All VMs (${this.userVMs.length})`,
      message: `You are about to permanently destroy all ${this.userVMs.length} VM(s) and their associated resources in this class. ` +
               `This action is irreversible and will delete all virtual machines, release all IP addresses, and remove all network interfaces.`,
      type: 'error',
      confirmPhrase: 'DESTROY ALL',
      confirmButtonText: 'Destroy All VMs',
      secondaryOption: {
        phrase: 'DESTROY ALL WITH DISKS',
        buttonText: 'Destroy All + OS Disks'
      }
    }).subscribe(result => {
      if (!result.confirmed) {
        this.notification.notify('Destruction cancelled', 'info', 2000);
        return;
      }
      this.destroyOSDisk = result.selectedOption === 'secondary';
      this.executeDestroyAllResources();
    });
  }

  // Execute the actual destruction of all resources
  private executeDestroyAllResources() {
    this.notification.notify('Starting destruction of all resources...', 'warning', 3000);

    // Show loading state
    this.loading = true;

    // Call the API to destroy all VMs (studentId = "all", destroyOSDisk = false)
    this.classResourcesService.destroyVM(this.classId, 'all', this.destroyOSDisk).subscribe({
      next: (response) => {
        this.loading = false;
        this.userVMs = []; // Clear the list
        this.notification.notify('🎉 Successfully destroyed all resources!', 'success', 4000);
        // Optionally refresh the data
        this.getResourcesByClassId();
      },
      error: (error) => {
        this.loading = false;
        this.logger.e('Failed to destroy all resources:', error);
        this.notification.notify('Failed to destroy all resources', 'error', 3000);
      }
    });
  }

  viewAccountDetails(acc: any) {
    // TODO: Implement view details logic
    alert('View details for account: ' + acc.participant);
  }

  destroyAccount(acc: any) {
    if (!acc) {
      this.notification.notify('Invalid account data', 'error', 3000);
      return;
    }

    this.confirmDialog.typedConfirm({
      title: `Destroy Account for ${acc.participant}`,
      message: `You are about to permanently destroy the user account for participant "${acc.participant}". ` +
               `This action is irreversible and will delete the user account, remove the subscription, revoke all access permissions, ` +
               `and clear all authentication data.`,
      details: [
        `Participant: ${acc.participant}`,
        `Invite Email: ${acc.inviteEmail}`,
        `Cloud Username: ${acc.cloudeUsername}`,
        `Subscription: ${acc.subscriptionId}`
      ],
      type: 'warning',
      confirmPhrase: 'DESTROY',
      confirmButtonText: 'Destroy Account'
    }).subscribe(result => {
      if (!result.confirmed) {
        this.notification.notify('Account destruction cancelled', 'info', 2000);
        return;
      }
      this.executeDestroyAccount(acc);
    });
  }

  // Execute the actual destruction of a specific user account
  private executeDestroyAccount(acc: any) {
    this.notification.notify(`Starting destruction of account for user "${acc.userId}"...`, 'warning', 3000);

    // Show loading state
    this.loading = true;

    // Call the API to destroy the specific user account
    this.classResourcesService.destroyUserAccount(acc.userId, this.classId).subscribe({
      next: (response) => {
        this.loading = false;
        // Remove the destroyed account from the list
        this.userAccounts = this.userAccounts.filter(accItem => accItem.userId !== acc.userId);
        this.notification.notify(`🎉 Successfully destroyed account for user "${acc.userId}"!`, 'success', 4000);
        // Optionally refresh the data
        this.getResourcesByClassId();
      },
      error: (error) => {
        this.loading = false;
        this.logger.e('Failed to destroy account:', error);
        this.notification.notify(`Failed to destroy account for "${acc.participant}"`, 'error', 3000);
      }
    });
  }

  // Destroy all user accounts and resources in the current class
  destroyAllAccounts() {
    if (!this.userAccounts || this.userAccounts.length === 0) {
      this.notification.notify('No user accounts found to destroy', 'warning', 3000);
      return;
    }

    this.confirmDialog.typedConfirm({
      title: `Destroy All Accounts (${this.userAccounts.length})`,
      message: `You are about to permanently destroy all ${this.userAccounts.length} user account(s) and their associated resources. ` +
               `This action is irreversible and will delete all user accounts, remove all subscriptions, revoke all access permissions, ` +
               `and clear all authentication data.`,
      type: 'error',
      confirmPhrase: 'DESTROY ALL',
      confirmButtonText: 'Destroy All Accounts'
    }).subscribe(result => {
      if (!result.confirmed) {
        this.notification.notify('Account destruction cancelled', 'info', 2000);
        return;
      }
      this.executeDestroyAllAccounts();
    });
  }

  // Execute the actual destruction of all user accounts
  private executeDestroyAllAccounts() {
    this.notification.notify('Starting destruction of all user accounts...', 'warning', 3000);
    this.loading = true;
    this.classResourcesService.destroyUserAccount('all', this.classId).subscribe({
      next: (response) => {
        this.loading = false;
        this.userAccounts = [];
        this.notification.notify('🎉 Successfully destroyed all user accounts!', 'success', 4000);
        this.getResourcesByClassId();
      },
      error: (error) => {
        this.loading = false;
        this.logger.e('Failed to destroy all user accounts:', error);
        this.notification.notify('Failed to destroy all user accounts', 'error', 3000);
      }
    });
  }

  // Get total cost for display
  getTotalCost(): number {
    return this.subscriptions.reduce((total, sub) => total + sub.totalCostINR, 0);
  }

  // Get resource type icon/label
  getResourceTypeLabel(resourceId: string): string {
    if (resourceId.includes('virtualmachines')) return 'VM';
    if (resourceId.includes('disks')) return 'Disk';
    if (resourceId.includes('snapshots')) return 'Snapshot';
    if (resourceId.includes('publicipaddresses')) return 'Public IP';
    if (resourceId.includes('networkinterfaces')) return 'Network Interface';
    return 'Other';
  }

  // Parse float for template
  parseFloat(value: string): number {
    return parseFloat(value);
  }

  // Initialize costing data (mock data until API is available)
  initializeCostingData() {
    this.costingData = [
      {
        subscriptionId: '4358894898',
        type: 'VM',
        billing: 30000
      },
      {
        subscriptionId: 'dhdjk8945090',
        type: 'UA',
        billing: 400
      }
    ];
  }

  /**
   * Loads per-user Azure resource usage for the User Account subscriptions tab.
   * Uses the real UserAccountUsageByClassId API endpoint.
   * Falls back to empty data (no hardcoded dummy data) if the call fails.
   */
  loadUserAccountUsage(online: boolean = false): void {
    if (!this.classId) return;

    this.classResourcesService.getUserAccountUsageByClassId(this.classId, online).subscribe({
      next: (response: any) => {
        this.logger.i('User account usage response:', response);

        const rows: any[] = response?.Data ?? [];

        // Map API response to the shape the template expects for userWiseUsage
        this.userWiseUsage = rows.map((row: any) => ({
          userId: row.userId ?? row.subscriptionId ?? '',
          userName: row.userName ?? row.subscriptionId ?? '',
          email: row.userEmail ?? '',
          used: typeof row.totalCostINR === 'number' ? row.totalCostINR : parseFloat(row.totalCostINR ?? '0'),
          quota: row.quota ?? null,
          currency: row.currency ?? 'INR',
          subscriptionId: row.subscriptionId ?? '',
          resources: row.resources ?? []
        }));

        // Recalculate totals
        const totalUsedAmount = this.userWiseUsage.reduce((sum, u) => sum + (u.used ?? 0), 0);
        const totalQuotaAmount = this.userWiseUsage.reduce((sum, u) => sum + (u.quota ?? 0), 0);
        this.totalUsage = {
          totalQuota: totalQuotaAmount,
          totalUsed: totalUsedAmount,
          remainingQuota: totalQuotaAmount - totalUsedAmount,
          utilizationPercentage: totalQuotaAmount > 0
            ? ((totalUsedAmount / totalQuotaAmount) * 100).toFixed(1)
            : '0'
        };

        this.userWiseResources = [];
        this.selectedUser = null;
      },
      error: (err) => {
        this.logger.e('Failed to load user account usage:', err);
        // Don't replace the placeholder data on error — leave zeroes shown
        this.userWiseUsage = [];
        this.totalUsage = {};
        this.userWiseResources = [];
      }
    });
  }

  // Select user and show their resources
  selectUser(user: any) {
    this.selectedUser = user;
    this.userWiseResources = user.resources || [];
  }

  // Select subscription for costing details
  selectSubscription(subscription: any) {
    this.selectedSubscription = subscription;
    this.selectedResources = subscription.resources || [];
  }

  getRightsByCases(): void {
    const userId = localStorage.getItem('userId') || '';

    const casesList = [RightsCases.deleteResource]
    this.userService.rightsCasesGetByUserId(userId, casesList)
      .subscribe({
        next: (result) => {
          this.logger.i('check Rights Result:', result);
          const resultSet = result;
          this.isVisibleDeteleResourceButton = resultSet.deleteResource;
        },
        error: (err) => {
          this.logger.e('Error loading in check Rights:', err);
        }
      });
    this.logger.i('check rights', this.isVisibleDeteleResourceButton);
  }
}
